import * as React from "react";
import {cx}       from "emotion";

import "./tab-photo-integrations.scss";

import {Types}             from "@Common/types";
import {consumeContext}    from "@Common/react/context";
import {Load2}             from "@Common/react/load2";
import {ComponentLink,
        Link, makeChain}   from "@Common/react/cst2";
import {UseRef}            from "@Common/react/use-ref";
import {UseState}          from "@Common/react/use-state";
import {Select}            from "@Common/ui-components/heavy/common/design2/select/select";
import {ButtonSpinner}     from "@Common/ui-components/loading-spinners/button-spinner";
import {IconDefinition}    from "@fortawesome/fontawesome-svg-core";
import {faChevronUp,
        faCloud,
        faLinkSlash,
        faSignHanging,
        faPlus}            from "@fortawesome/free-solid-svg-icons";
import {faGoogleDrive,
        faDropbox,
        faUnsplash}        from "@fortawesome/free-brands-svg-icons";
import {FontAwesomeIcon}   from "@fortawesome/react-fontawesome";

import {ContentArea}             from "../components/content-area";
import {ErrorMessage}            from "../components/error-message";
import {GalleryImage}            from "../components/gallery-image";
import {GoogleDriveSelectorCard} from "../components/google-drive-selector-card";
import {LoadingSpinner}          from "../components/loading-spinner";
import {MultiSelectFooter}       from "../components/multi-select-footer";
import {NoticeMessage}           from "../components/notice-message";
import {PhotoIntegrationTile}    from "../components/photo-integration-tile";
import {FeedbackForm}            from "../components/feedback-form";


export const TabPhotoIntegrations = ({
    modal,
    viewType,
    numResultsPerPage,
    imageArea             = null,
    singleSelectOptions   = null,
    multiSelectOptions    = null,
    orderId,
    isMobile,
    disallowSvgImages     = false,
    onPaginateNumResults,
}:{
    modal                 : Types.Common.UiComponents.ModalService3.ModalService3Next<Types.Common.UiComponents.PhotoDashboard.Models.PhotoDashboardData>,
    viewType              : Types.Common.UiComponents.PhotoDashboard.viewTypes,
    numResultsPerPage     : Types.Common.UiComponents.PhotoDashboard.numResultsPerPage,
    imageArea            ?: Types.Common.UiComponents.PhotoDashboard.Models.ImageArea,
    singleSelectOptions  ?: Types.Common.UiComponents.PhotoDashboard.Models.SingleSelectOptions,
    multiSelectOptions   ?: Types.Common.UiComponents.PhotoDashboard.Models.MultiSelectOptions,
    orderId               : number,
    isMobile              : boolean,
    disallowSvgImages    ?: boolean,
    onPaginateNumResults  : Types.Common.UiComponents.PhotoDashboard.Functions.OnPaginateNumResults,
}) => (

    // @ts-ignore to prevent TS error: Type instantiation is excessively deep and possibly infinite. ts(2589)
    makeChain<JSX.Element>()

    .then(...consumeContext("apis"))
    .then(...consumeContext("systemConstants"))
    .then(...consumeContext("company"))

    // set state vars
    .then("state", (_, next:ComponentLink<typeof UseState<{
        errorMessage              : string,
        photoServiceId            : number,
        loginUserId               : string,
        isLoginSubmitting         : boolean,
        isLoginSwitchingAccounts  : boolean,
        isLoginDisconnecting      : boolean,
        isImageLoadingToEditor    : boolean,
        view                      : "results" | "manage" | "login" | "feedback",
        folderId                  : string,
        searchString              : string,
        pageNumber                : number,
    }>>) => UseState({
        defaultValue: {
            errorMessage: "",
            photoServiceId: 0,
            loginUserId: "",
            isLoginSubmitting: false,
            isLoginSwitchingAccounts: false,
            isLoginDisconnecting: false,
            isImageLoadingToEditor: false,
            view: "results",
            folderId: "",
            searchString: "",
            pageNumber: 1,
        },
        next
    }))

    // display loader while applying images to the editor
    .then(({state}, next:Link<JSX.Element>) => {

        if (state.value.isImageLoadingToEditor) {
            return (
                <div className="tab-content photo-integrations">
                    <div className="results">
                        <LoadingSpinner text="Applying Image"/>
                    </div>
                </div>
            );
        }

        return next();
    })

    // load integrations
    .then("loaderIntegrations", ({state, apis}, next:ComponentLink<typeof Load2<Types.Apis.Models.GalleryIntegration[]>>) => Load2({
        fetch: async () => {
            const integrations = await apis.next.gallery.integrations.getAll();

            // show error message if error returned
            if (integrations.statusCode) {
                state.onChange({...state.value, errorMessage: integrations.message});
                return null;
            }

            // default to the manage screen if no integrations saved to the account
            if (!integrations.filter(x => !!x.loginUserId).length) {
                state.onChange({...state.value, view: "manage"});
            }

            return integrations;

        },
        captureException: true,
        next,
    }))

    // filter integrations
    .then("userIntegrations", ({loaderIntegrations}, next:Link<JSX.Element, Types.Apis.Models.GalleryIntegration[]>) => next(
        loaderIntegrations?.value?.filter(integration => !!integration.loginUserId) || []
    ))
    .then("standardIntegrations", ({loaderIntegrations}, next:Link<JSX.Element, Types.Apis.Models.GalleryIntegration[]>) => next(
        loaderIntegrations?.value?.filter(integration => !integration.isPropertyService) || []
    ))
    .then("propertyServiceIntegrations", ({loaderIntegrations}, next:Link<JSX.Element, Types.Apis.Models.GalleryIntegration[]>) => next(
        loaderIntegrations?.value?.filter(integration => integration.isPropertyService) || []
    ))
    .then("selectedPhotoService", ({state, loaderIntegrations}, next:Link<JSX.Element, Types.Apis.Models.GalleryIntegration>) => next(
        loaderIntegrations?.value?.find(integration => integration.photoServiceId === state.value.photoServiceId) || null
    ))

    // prepare loader for photo service folders
    .then("loaderFolders", ({state, selectedPhotoService, apis}, next:ComponentLink<typeof Load2<Types.Apis.Models.GalleryIntegrationFolder[]>>) => Load2({
        _key: "folders_" + state.value.photoServiceId + "_" + state.value.view + "_" + (state.value.isLoginSubmitting ? ("_"+state.value.loginUserId) : ""),
        fetch: async () => {
            if ( (!selectedPhotoService.loginUserId && selectedPhotoService.authMethod != 'oauth' )
                || !selectedPhotoService.usesFolders
                || state.value.view !== "results"
            ) return [];
            const folders = await apis.next.gallery.integrations.getFolders({
                serviceName: selectedPhotoService.shortName,
                orderId
            });

            // return to the add integration view if oauth and an error was encountered
            if (folders.statusCode && selectedPhotoService.authMethod === "oauth") {
                state.onChange({
                    ...state.value,
                    view: "login",
                    isLoginSubmitting: false,
                    errorMessage: `Could not access your account. Use the switch account button to reconnect.`
                });
                return [];
            }

            // if no folders and a property photo service, display error message
            if (folders.statusCode) {
                state.onChange({
                    ...state.value,
                    isLoginSubmitting: false,
                    errorMessage: `Account not found on ${selectedPhotoService.serviceName} or no properties were available. Please review your ${selectedPhotoService.authMethod === "apikey" ? "API key" : "email address"}.`
                });
                return[];
            }

            // return folders if lookup was successful
            state.onChange({...state.value, view: "results", isLoginSubmitting: false});
            return folders;

        },
        captureException: true,
        next,
    }))

    // prepare loader for photo service images
    .then("loaderPhotos", ({apis, state, selectedPhotoService}, next:ComponentLink<typeof Load2<Types.Apis.Models.GalleryImagePhotoService[]>>) => Load2({
        _key: "images_" + state.value.photoServiceId + "_" + state.value.loginUserId + "_" + state.value.folderId,
        fetch: async () => {
            if (!selectedPhotoService.loginUserId) return [];

            const images = !!state.value.searchString ?
                await apis.next.gallery.integrations.searchImages({
                    serviceName: selectedPhotoService.shortName,
                    search: state.value.searchString,
                    orderId
                }) :
                await apis.next.gallery.integrations.getImages({
                    serviceName: selectedPhotoService.shortName,
                    folderId: state.value.folderId,
                    orderId
                });

            // return to the add integration view if oauth and an error was encountered
            if (images.statusCode && selectedPhotoService.authMethod === "oauth") {
                const additionalMessage = (selectedPhotoService.shortName === "GoogleDrive")
                    ? "While signing in, you must select the checkbox stating: \"See, edit, create, and delete only the specific Google Drive files you use with this app.\""
                    : "";
                state.onChange({
                    ...state.value,
                    view: "login",
                    isLoginSubmitting: false,
                    errorMessage: `Could not access your account. Use the switch account button to reconnect. ${additionalMessage}`
                });
                return [];
            }

            // if no folders and a property photo service, display error message
            if (images.statusCode) {
                state.onChange({
                    ...state.value,
                    isLoginSubmitting: false,
                    errorMessage: `Account not found on ${selectedPhotoService.serviceName} or no properties were available. Please review your ${selectedPhotoService.authMethod === "apikey" ? "API key" : "email address"}.`
                });
                return[];
            }

            return images;

        },
        captureException: true,
        next,
    }))

    // create function to load the integration icon
    .then("getIcon", (_, next:Link<JSX.Element, (s:string)=>IconDefinition>) => next(serviceName => {
        switch (serviceName) {
            case "GoogleDrive": return faGoogleDrive;
            case "Dropbox": return faDropbox;
            case "OneDrive": return faCloud;
            case "Unsplash": return faUnsplash;
            default: return faSignHanging;
        }
    }))

    // create function to prompt OAuth login
    .then("promptOAuthLogin", ({systemConstants}, next:Link<JSX.Element, (s:string)=>Window|Promise<Window>>) => next(serviceName => {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.innerWidth - width) / 2;
        const top = window.screenY + (window.innerHeight - height) / 2;
        switch (serviceName) {
            case "GoogleDrive":
                if (!systemConstants?.value?.GOOGLE_CLIENT_ID) return;

                return window.open(
                    (
                        "https://accounts.google.com/o/oauth2/auth" +
                        "?client_id=" + systemConstants.value.GOOGLE_CLIENT_ID +
                        "&redirect_uri=" + encodeURIComponent(window.location.origin + "/app/photo-integration-auth?service=GoogleDrive") +
                        "&scope=" + encodeURIComponent([
                            "https://www.googleapis.com/auth/drive.file",
                            "https://www.googleapis.com/auth/userinfo.email"
                        ].join(" ")) +
                        "&response_type=code" +
                        "&access_type=offline" +
                        "&prompt=consent"
                    ),
                    "GoogleLogin",
                    `width=${width},height=${height},top=${top},left=${left},resizable,scrollbars`
                );

                break;
            case "OneDrive":
                if (!systemConstants?.value?.ONEDRIVE_CLIENT_ID) return;

                return (function initiateOneDriveAuth() {
                    const generateCodeVerifier = (length = 128) => {
                        const array = new Uint32Array(length);
                        window.crypto.getRandomValues(array);
                        return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
                    };

                    const generateCodeChallenge = async (codeVerifier: string) => {
                        const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
                        return btoa(String.fromCharCode(...new Uint8Array(buffer)))
                            .replace(/\+/g, '-')
                            .replace(/\//g, '_')
                            .replace(/=+$/, '');
                    };

                    const codeVerifier = generateCodeVerifier();  // Synchronously generate the code verifier

                    // Open the window immediately with a blank URL and return the reference
                    const windowRef = window.open(
                        "about:blank",
                        "OneDriveLogin",
                        `width=${width},height=${height},top=${top},left=${left},resizable,scrollbars`
                    );

                    // Generate the code challenge asynchronously and update the window's URL once ready
                    generateCodeChallenge(codeVerifier).then(codeChallenge => {
                        const redirectUri = `${window.location.origin}/app/photo-integration-auth?service=OneDrive&code_verifier=${codeVerifier}`;
                        const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${new URLSearchParams({
                            client_id: systemConstants.value.ONEDRIVE_CLIENT_ID,
                            response_type: 'code',
                            redirect_uri: redirectUri,
                            response_mode: 'query',
                            code_challenge: codeChallenge,
                            code_challenge_method: 'S256',
                            scope: systemConstants.value.ONEDRIVE_SCOPE
                        }).toString()}`;

                        if (windowRef) {
                            windowRef.location.href = authUrl;  // Update the window's URL
                        }
                    });

                    return windowRef;  // Return the window reference immediately
                })();
                break;
            case "Dropbox":
                if (!systemConstants?.value?.DROPBOX_CLIENT_ID) return;

                return window.open(
                    (
                        "https://www.dropbox.com/oauth2/authorize" +
                        "?client_id=" + systemConstants.value.DROPBOX_CLIENT_ID +
                        "&redirect_uri=" + encodeURIComponent(window.location.origin + "/app/photo-integration-auth?service=Dropbox") +
                        "&response_type=code" +
                        "&token_access_type=offline" +
                        "&scope=" + systemConstants.value.DROPBOX_SCOPE +
                        "&force_reapprove=true"
                    ),
                    "DropboxLogin",
                    `width=${width},height=${height},top=${top},left=${left},resizable,scrollbars`
                );
                break;
            case "Unsplash":
                if (!systemConstants?.value?.UNSPLASH_CLIENT_ID) return;
                return window.open(
                    (
                        "https://unsplash.com/oauth/authorize" +
                        "?client_id=" + systemConstants.value.UNSPLASH_CLIENT_ID +
                        "&redirect_uri=" + encodeURIComponent(window.location.origin + "/app/photo-integration-auth?service=Unsplash") +
                        "&response_type=code" +
                        "&scope=" + systemConstants.value.UNSPLASH_SCOPE +
                        "&prompt=login"
                    ),
                    "UnsplashLogin",
                    `width=${width},height=${height},top=${top},left=${left},resizable,scrollbars`
                );
                break;
        }
    }))

    // check if pagination is required
    .then("includePagination", ({loaderPhotos}, next:Link<JSX.Element, boolean>) => next(isMobile && !!loaderPhotos.value?.length))

    // build the manage services button
    .then("buttonManageServices", ({state, loaderFolders, loaderPhotos}, next:Link<JSX.Element>) => next(
        <button className="manage-services" onClick={() => !loaderFolders.loading && !loaderPhotos.loading &&
            state.onChange({
                ...state.value,
                view: "manage",
                photoServiceId: 0,
                folderId: "",
                searchString: "",
                pageNumber: 1,
                loginUserId: "",
                errorMessage: "",
            })
        }>Manage Services</button>
    ))

    // build the sidebar
    .then("contentSidebar", ({state, loaderFolders, loaderPhotos, userIntegrations, selectedPhotoService, getIcon, buttonManageServices},
        next:Link<JSX.Element>) => next(
            userIntegrations.length > 0 &&
            (!isMobile || ["results", "manage"].includes(state.value.view)) && ( // for mobile, don't show for login or feedback views
        <div className="sidebar no-padding">
            {!isMobile ? <>
                <ul className="integration-list">
                    {userIntegrations.map(integration => (
                        <li
                            className={cx("folder", {
                                "active": state.value.view === "results" && integration.photoServiceId === selectedPhotoService?.photoServiceId,
                                "has-selection": !!state.value.folderId
                            })}
                            key={"sidebar-"+integration.photoServiceId}
                            onClick={() => !loaderFolders.loading && !loaderPhotos.loading && state.onChange({
                                ...state.value,
                                view: "results",
                                photoServiceId: integration.photoServiceId,
                                loginUserId: integration.loginUserId,
                                folderId: "",
                                pageNumber: 1,
                                searchString: "",
                                errorMessage: "",
                            })}
                        >
                            <div className="service">
                                <div className="service-name">
                                    <i className="icon">
                                        <FontAwesomeIcon icon={getIcon(integration.shortName)}/>
                                    </i>
                                    <span>{integration.serviceName}</span>
                                </div>
                                {state.value.view === "results" && integration.photoServiceId === selectedPhotoService?.photoServiceId && loaderFolders.value?.length > 0 && (
                                    <FontAwesomeIcon icon={faChevronUp}/>
                                )}
                            </div>
                            {state.value.view === "results"
                                && integration.photoServiceId === selectedPhotoService?.photoServiceId
                                && loaderFolders.value?.length > 0 && (
                                <div className="scroll-area">
                                    <ul className="subfolders">
                                        {loaderFolders.value?.map(folder => (
                                            <li
                                                className={cx("subfolder", {"active": folder.id === state.value.folderId})}
                                                key={"sidebar-" + integration.photoServiceId + "-" + folder.id}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    if (loaderPhotos.loading || loaderFolders.loading) return;
                                                    state.onChange({
                                                        ...state.value,
                                                        view: "results",
                                                        folderId: folder.id,
                                                        searchString: "",
                                                        pageNumber: 1,
                                                        errorMessage: "",
                                                    });
                                                }}>
                                                <i className={cx("fa", (folder.id === state.value.folderId) ? "fa-folder-open" : "fa-folder")}></i>
                                                <span title={folder.name}>{folder.name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
                {buttonManageServices}
            </> : <>
                <div>
                    <h2>Service</h2>
                    <Select
                        placeholder="Select a Service"
                        list={userIntegrations.map(integration => ({
                            id: integration.photoServiceId,
                            name: integration.serviceName,
                            display: <div className="service">
                                <i className="icon">
                                    <FontAwesomeIcon icon={getIcon(integration.shortName)}/>
                                </i>
                                <span>{integration.serviceName}</span>
                                </div>
                        }))}
                        itemToLabel={v => v.display}
                        isSelected={v => v.id === selectedPhotoService?.photoServiceId}
                        onChange={v => !loaderPhotos.loading && !loaderFolders.loading && state.onChange({
                            ...state.value,
                            view: "results",
                            photoServiceId: v.id,
                            loginUserId: userIntegrations.find(x => x.photoServiceId === v.id)?.loginUserId,
                            folderId: "",
                            pageNumber: 1,
                            errorMessage: "",
                        })}
                        captureOutsideClicks={true}
                        xdQaName="Service"
                    />
                </div>
                {loaderFolders.value?.length > 0 && <>
                    <div>
                        <h2>Folder</h2>
                        <Select
                            placeholder="Select a Folder"
                            list={loaderFolders.value.map(folder => ({
                                id: folder.id,
                                name: folder.name,
                                display: <div className="service">
                                    <i className={cx("fa fa-folder")}></i>
                                    <span>{folder.name}</span>
                                    </div>
                            }))}
                            itemToLabel={v => v.display}
                            isSelected={v => v.id === state.value.folderId}
                            onChange={v => !loaderPhotos.loading && !loaderFolders.loading && state.onChange({
                                ...state.value,
                                view: "results",
                                folderId: v.id,
                                searchString: "",
                                pageNumber: 1,
                                errorMessage: "",
                            })}
                            captureOutsideClicks={true}
                            xdQaName="Folder"
                        />
                    </div>
                </>}
            </>}
        </div>
    )))

    // create function for applying changes
    .then("getIntegrationTiles", ({state, loaderIntegrations, promptOAuthLogin}, next:Link<JSX.Element, (i:Types.Apis.Models.GalleryIntegration[], n:number) => JSX.Element[]>) => next(
        (integrations, numOld) => integrations.map(integration => (
            <PhotoIntegrationTile
                key={"tile-" + integration.photoServiceId}
                id={integration.photoServiceId}
                serviceName={integration.serviceName}
                serviceLogo={integration.serviceLogo}
                isPropertyService={integration.isPropertyService || isMobile} // mobile forces all integrations to same format
                isRegistered={!!integration.loginUserId}
                isNew={integration.isNew && numOld > 0} // mark as new if integration is new, unless all are new, then omit to prevent the "new" badge from displaying on every tile
                onClick={() => {

                    // load oauth login screen if oauth service not already registered
                    if (!integration.loginUserId && integration.authMethod === "oauth") {
                        localStorage.setItem("oauthCompleted", "false");
                        const authWindow = promptOAuthLogin(integration.shortName);
                        if (!authWindow) return;
                        const checkWindowClosed = setInterval(() => {
                            if (authWindow.closed) {
                                clearInterval(checkWindowClosed);

                                const completed = localStorage.getItem("oauthCompleted") === "true";
                                localStorage.removeItem("oauthCompleted");
                                if (!completed) return;

                                loaderIntegrations.reload();
                                state.onChange({...state.value, view: "results", photoServiceId: integration.photoServiceId});
                            }
                        }, 500);
                    }

                    // show the standard login form
                    else state.onChange({
                        ...state.value,
                        view: "login",
                        photoServiceId: integration.photoServiceId,
                        errorMessage: "",
                    });

                }}
            />
        ))
    ))

    // build the integrations options
    .then("contentIntegrationsList", ({state, getIntegrationTiles, loaderIntegrations, standardIntegrations, propertyServiceIntegrations, promptOAuthLogin}, next:Link<JSX.Element>) => next(
        state.value.view === "manage" && <>

            {!isMobile ? <>
                {standardIntegrations.length > 0 && (
                    <div className="standard-integrations">
                        {getIntegrationTiles(standardIntegrations, standardIntegrations.filter(x => !x.isNew).length + propertyServiceIntegrations.filter(x => !x.isNew).length)}
                    </div>
                )}
                {propertyServiceIntegrations.length > 0 && <>
                    <h3 className="section-separator">
                        <span className="text">Property Service Integrations</span>
                        <span className="line"></span>
                    </h3>
                    <div className="property-service-integrations">
                        {getIntegrationTiles(propertyServiceIntegrations, standardIntegrations.filter(x => !x.isNew).length + propertyServiceIntegrations.filter(x => !x.isNew).length)}
                        {loaderIntegrations?.value?.length > 0 && <>
                            <div className="card request-new-integration"
                                onClick={() => state.onChange({
                                    ...state.value,
                                    view: "feedback",
                                    errorMessage: "",
                                })}
                            >
                                <i className="icon">
                                    <FontAwesomeIcon icon={faPlus}/>
                                </i>
                                Request New Integration
                            </div>
                        </>}
                    </div>
                </>}
            </> : <> {/* all integrations are in a single list for mobile */}
                <div className="mobile-integrations">
                    {getIntegrationTiles(standardIntegrations, standardIntegrations.filter(x => !x.isNew).length + propertyServiceIntegrations.filter(x => !x.isNew).length)}
                    {getIntegrationTiles(propertyServiceIntegrations, standardIntegrations.filter(x => !x.isNew).length + propertyServiceIntegrations.filter(x => !x.isNew).length)}
                    {(standardIntegrations.length > 0 || propertyServiceIntegrations.length > 0) && (
                        <div className="card request-new-integration"
                            onClick={() => state.onChange({
                                ...state.value,
                                view: "feedback",
                                errorMessage: "",
                            })}
                        >
                            <i className="icon">
                                <FontAwesomeIcon icon={faPlus}/>
                            </i>
                            Request New Integration
                        </div>
                    )}
                </div>
            </>}
        </>
    ))

    // build the integration selection notice
    .then("contentIntegrationSelectionNotice", ({state, getIntegrationTiles, loaderIntegrations, standardIntegrations, propertyServiceIntegrations, selectedPhotoService, promptOAuthLogin}, next:Link<JSX.Element>) => next(
        state.value.view === "results" && !selectedPhotoService && <>

            {loaderIntegrations.loading &&
                <LoadingSpinner text="Loading Integrations"/>
            }
            {loaderIntegrations.value?.length === 0 &&
                <NoticeMessage text="No integrations were found."/>
            }
            {loaderIntegrations.value?.length > 0 &&
                <NoticeMessage text="Select an integration to view images."/>
            }

        </>
    ))

    // build the login form
    .then("loginSubmitRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext|any>) => UseRef({next}))
    .then("contentLoginForm", ({state, apis, userIntegrations, loaderIntegrations, selectedPhotoService, promptOAuthLogin, loaderPhotos, loaderFolders, loginSubmitRef},
        next:Link<JSX.Element>) => next(state.value.view === "login" && selectedPhotoService && (
        <ContentArea backText="Back to All Services"
            onBack={() => state.onChange({
                ...state.value,
                view: "manage",
                photoServiceId: 0,
                isLoginSubmitting: false,
                isLoginSwitchingAccounts: false,
                errorMessage: "",
            })}
        >
            <div className="login-form">
                <img className="logo"
                    src={selectedPhotoService.serviceLogo} alt={selectedPhotoService.serviceName}
                />
                {!!selectedPhotoService.loginUserId && !state.value.isLoginSwitchingAccounts ? <>
                    <div className="login-info">
                        <p className="label">Logged in as</p>
                        <p className="value">{selectedPhotoService.loginUserId}</p>
                    </div>
                </> : <>
                    <div className="line">
                        <input className="field"
                            xd-qa-type="textbox" xd-qa-name="ccgallery photo integration login field"
                            placeholder={selectedPhotoService.authMethod === "apikey" ? "API Key" : "Email Address"}
                            maxLength={60}
                            value={state.value.loginUserId}
                            onChange={event => !state.value.isLoginSubmitting && state.onChange({
                                ...state.value,
                                loginUserId: event.target.value,
                                errorMessage: "",
                            })}
                            onKeyPress={e => {
                                if (e.key !== "Enter") return;
                                e.preventDefault();
                                loginSubmitRef.get().click();
                            }}
                        />
                        <button ref={ref => loginSubmitRef.set(ref)}
                            className="button login"
                            xd-qa-type="button" xd-qa-name="ccgallery photo integration login submit"
                            disabled={state.value.isLoginSubmitting}
                            onClick={async () => {
                                if (!state.value.loginUserId) return;
                                state.onChange({...state.value, isLoginSubmitting: true, errorMessage: ""});
                                await apis.next.gallery.integrations.insert({
                                    serviceName: selectedPhotoService.shortName,
                                    loginUserId: state.value.loginUserId
                                });
                                await loaderIntegrations.reload();
                                state.onChange({...state.value, isLoginSubmitting: false, isLoginSwitchingAccounts: false, view: "results"});
                            }}
                        >
                            {!state.value.isLoginSubmitting ? "Login" : (
                                <ButtonSpinner size={20} spinnerColor="#ffffff"/>
                            )}
                        </button>
                    </div>
                    <button className="cancel"
                        xd-qa-type="button" xd-qa-name="ccgallery photo integration login cancel"
                        onClick={event => {
                            event.preventDefault(); // prevent form submission
                            if (state.value.isLoginSwitchingAccounts)
                                state.onChange({...state.value, loginUserId: "", isLoginSwitchingAccounts: false, errorMessage: ""});
                            else state.onChange({...state.value, view: "manage", photoServiceId: 0, loginUserId: "", isLoginSubmitting: false, errorMessage: ""});
                        }}
                    >Cancel</button>
                </>}

                {selectedPhotoService.loginUserId && !state.value.isLoginSwitchingAccounts && (
                    <div className="line">
                        <button className="button switch-account"
                            xd-qa-type="button" xd-qa-name="ccgallery photo integration switch account"
                            onClick={async (event) => {
                                event.preventDefault(); // prevent form submission

                                // load oauth login screen if needed
                                if (selectedPhotoService.authMethod === "oauth") {
                                    localStorage.setItem("oauthCompleted", "false");
                                    const authWindow = promptOAuthLogin(selectedPhotoService.shortName);
                                    if (!authWindow) return;
                                    const checkWindowClosed = setInterval(() => {
                                        if (authWindow.closed) {
                                            clearInterval(checkWindowClosed);

                                            const completed = localStorage.getItem("oauthCompleted") === "true";
                                            localStorage.removeItem("oauthCompleted");
                                            if (!completed) return;

                                            state.onChange({
                                                ...state.value,
                                                isLoginSubmitting: true,
                                                isLoginSwitchingAccounts: false,
                                                view: "results",
                                                photoServiceId: selectedPhotoService.photoServiceId,
                                                errorMessage: "",
                                            });
                                            loaderIntegrations.reload();
                                            loaderFolders.reload();
                                            loaderPhotos.reload();
                                        }
                                    }, 500);
                                }
                                // provide standard form if not oauth
                                else state.onChange({
                                    ...state.value,
                                    loginUserId: selectedPhotoService.loginUserId,
                                    isLoginSwitchingAccounts: true,
                                    errorMessage: "",
                                });

                            }}
                        >Switch Account</button>
                        <button className="button disconnect"
                            xd-qa-type="button" xd-qa-name="ccgallery photo integration disconnect service"
                            disabled={state.value.isLoginDisconnecting}
                            onClick={async (event) => {
                                event.preventDefault(); // prevent form submission
                                if (!selectedPhotoService?.shortName) return;

                                state.onChange({
                                    ...state.value,
                                    isLoginDisconnecting: true,
                                    errorMessage: "",
                                });

                                // remove the service
                                const removeService = await apis.next.gallery.integrations.delete({serviceName: selectedPhotoService.shortName});
                                if (removeService?.statusCode) {
                                    state.onChange({...state.value, isLoginSubmitting: true, isLoginDisconnecting: false, errorMessage: removeService.message});
                                }

                                await loaderIntegrations.reload();
                                state.onChange({...state.value, view: "manage", photoServiceId: 0, loginUserId: "", isLoginSubmitting: false, isLoginDisconnecting: false});

                            }
                        }>
                            {!state.value.isLoginDisconnecting ? <>
                                <FontAwesomeIcon icon={faLinkSlash}/>Disconnect
                            </> : <>
                                <ButtonSpinner size={20} spinnerColor="#636e7a"/>
                            </>}
                        </button>
                    </div>
                )}
            </div>
        </ContentArea>
    )))

    // build the error message
    .then("contentErrorMessage", ({state},
        next:Link<JSX.Element>) => next(!!state.value.errorMessage &&

        <ErrorMessage text={state.value.errorMessage}/>

    ))

    // build the feedback form
    .then("contentFeedbackForm", ({state},
        next:Link<JSX.Element>) => next(state.value.view === "feedback" &&

        <FeedbackForm
            onClose={() => state.onChange({...state.value, view: "manage"})}
            surveyId={"hj-photo-integrations-survey"}
        />

    ))

    // build the image search form
    .then("searchSubmitRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext|any>) => UseRef({next}))
    .then("contentSearchForm", ({state, loaderIntegrations, loaderFolders, loaderPhotos, selectedPhotoService, searchSubmitRef},
        next:Link<JSX.Element>) => next(state.value.view === "results" && selectedPhotoService?.allowsSearch &&
            // only show search if the selected service is logged in - will not be if canceled out of oauth login
            loaderIntegrations.value?.find(x => x.photoServiceId === state.value.photoServiceId && x.loginUserId) &&

        <div className="search-form">
            <img className="logo"
                src={selectedPhotoService.serviceLogo} alt={selectedPhotoService.serviceName}
            />
            <input className="field"
                xd-qa-type="textbox"
                xd-qa-name="ccgallery photo integration search field"
                placeholder="Search Images"
                maxLength={200}
                value={state.value.searchString}
                onChange={event => !loaderPhotos.loading && !loaderFolders.loading && !loaderIntegrations.loading &&
                    state.onChange({...state.value, searchString: event.target.value})
                }
                onKeyPress={e => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    searchSubmitRef.get().click();
                }}
            />
            <button ref={ref => searchSubmitRef.set(ref)}
                className="button"
                disabled={loaderPhotos.loading || loaderFolders.loading || loaderIntegrations.loading}
                xd-qa-type="button" xd-qa-name="ccgallery photo integration search submit"
                onClick={() => {
                    state.onChange({...state.value, folderId: "", pageNumber: 1, errorMessage: ""});
                    loaderPhotos.reset();
                }}
            >
                {(!loaderPhotos.loading || !state.value.searchString) ? // don't show spinner if no search string
                    "Search" : (
                    <ButtonSpinner size={20} spinnerColor="#5bb3f6"/>
                )}
            </button>
        </div>

    ))

    // build the image results list
    .then("contentImageList", ({state, apis, company, selectedPhotoService, loaderFolders, loaderPhotos, includePagination},
        next:Link<JSX.Element>) => {

        // skip if not in results view or no service selected
        if (state.value.view !== "results" || !selectedPhotoService || !selectedPhotoService.loginUserId)
            return next();

        // display loading status
        if (loaderFolders.loading) return next(<LoadingSpinner text="Loading Folders"/>);
        else if (loaderPhotos.loading) return next(<LoadingSpinner text="Loading Photos"/>);

        // Google Drive has a file selector, so continue even if no images found, so that the selector will be available
        const allowEmptyGallery = selectedPhotoService.shortName === "GoogleDrive";

        // display notices
        if (selectedPhotoService.usesFolders && !loaderFolders.value?.length)
            return next(<NoticeMessage text="No folders were found."/>);
        else if (selectedPhotoService.usesFolders && !state.value.folderId
            && !loaderPhotos.value?.length // photos may be at root level, so don't prompt to select folder if any root photos are available
        )
            return next(<NoticeMessage text="Select a folder to view images."/>);
        else if (!loaderPhotos.value?.length && !allowEmptyGallery)
            return next(<NoticeMessage text="No images were found."/>);

        // retrieve the image data from the loader
        let images = loaderPhotos.value;
        if (includePagination) images = images.slice((state.value.pageNumber-1)*numResultsPerPage, state.value.pageNumber*numResultsPerPage);

        // return notice for empty galleries
        if (!images?.length && !allowEmptyGallery) return next();

        // display the images when available
        return next(
            <div className="source-images">
                {/* display a card for selecting files from Google Drive */}
                {selectedPhotoService.shortName === "GoogleDrive" && (
                    <GoogleDriveSelectorCard disallowSvgImages={disallowSvgImages} onComplete={() => {
                        loaderPhotos.reset();
                    }}/>
                )}
                {loaderPhotos.value.map((image) => (
                    <GalleryImage
                        key={image.id}
                        id={image.id}
                        thumbnailUrl={image.thumbnailUrl}
                        imageName={image.name}
                        imageAttributionData={image.attribution}
                        viewType={viewType}
                        width={image.width}
                        height={image.height}
                        fileType={image.fileType}
                        fileSize={image.fileSize}
                        areaWidth={imageArea?.widthInches}
                        areaHeight={imageArea?.heightInches}
                        photoQualitySettings={imageArea?.photoQualitySettings}
                        allowLowQuality={company.prefs.allowLowQualityPhotosProperty}
                        disabled={image.fileType === "svg" && disallowSvgImages}
                        selectedIndex={(!multiSelectOptions) ? 0 : multiSelectOptions.selectedImages.findIndex(x => x.assetType === "order" && x.originalImageId === image.id)+1}
                        lazyLoad={true}
                        onClick={async () => {

                            // apply the image for user-created templates
                            if (singleSelectOptions && modal.params.adminTool) {
                                // apply the image
                                singleSelectOptions.onApply(imageArea, "order", parseInt(image.id), image.printUrl, modal.close);
                            }

                            // apply the image for single select mode
                            else if (singleSelectOptions) {

                                // set the image as applying
                                state.onChange({
                                    ...state.value,
                                    isImageLoadingToEditor: true,
                                    errorMessage: "",
                                });

                                // save the image as an image asset
                                const savedImage = await apis.next.gallery.photoServices.savePhotoServiceImage(
                                    selectedPhotoService.photoServiceId,
                                    state.value.folderId,
                                    image.id,
                                    selectedPhotoService.loginUserId,
                                    image.printUrl,
                                    image.name,
                                    image.attribution,
                                    orderId
                                );
                                if (!savedImage?.id) {
                                    state.onChange({...state.value, isImageLoadingToEditor: false, errorMessage: "There was an error loading your photo. Please try another."});
                                    return;
                                }

                                // apply the image to the editor
                                singleSelectOptions.onApply(imageArea, "order", savedImage.id, savedImage.printUrl, modal.close);

                            }

                            // select the image for multi-select mode
                            else if (multiSelectOptions) {

                                // remove the current selection if already selected
                                const existingSelection = multiSelectOptions.selectedImages.find(x => x.assetType === "order" && x.originalImageId === image.id);
                                if (existingSelection) return multiSelectOptions.onSelect(existingSelection);

                                // TODO: Don't save image until finalizing the import - This creates a delay and will create a new order gallery image for selection if they select-unselect-select-etc
                                const savedImage = await apis.next.gallery.photoServices.savePhotoServiceImage(
                                    selectedPhotoService.photoServiceId,
                                    state.value.folderId,
                                    image.id,
                                    selectedPhotoService.loginUserId,
                                    image.printUrl,
                                    image.name,
                                    image.attribution,
                                    orderId
                                );
                                if (!savedImage?.id) return;

                                multiSelectOptions.onSelect({
                                    assetType: "order",
                                    imageId: savedImage.id,
                                    imagePath: savedImage.printUrl,
                                    thumbnailUrl: savedImage.thumbnailUrl,
                                    originalImageId: image.id,
                                });

                            }

                        }}
                        onThumbnailLoadError={({currentTarget}) => {

                            // try the print url as a fallback if the thumbnail can't be loaded
                            if (currentTarget.src !== image.printUrl) {
                                currentTarget.src = image.printUrl;
                            }
                            else currentTarget.parentElement.style.display = "none";

                        }}
                    />)
                )}
            </div>
        );
    })

    // render the tab contents
    .then(({state, contentSidebar, contentIntegrationsList, contentIntegrationSelectionNotice, contentLoginForm, contentFeedbackForm, contentImageList, contentSearchForm, contentErrorMessage, buttonManageServices}) => (<>

        <div className={cx("tab-photo-integrations-ptg tab-content photo-integrations viewtype-"+viewType, {"has-sidebar": !!contentSidebar})}>
            {contentSidebar}
            <div className="results">
                {contentErrorMessage}
                {contentLoginForm}
                {contentFeedbackForm}
                {contentIntegrationSelectionNotice}
                {contentIntegrationsList}
                {contentSearchForm}
                {contentImageList}
            </div>
            {isMobile && state.value.view === "results" && (
                buttonManageServices
            )}
        </div>
        {multiSelectOptions && (
        <MultiSelectFooter
            maxSelections={multiSelectOptions.maxSelections}
            numSelected={multiSelectOptions.selectedImages.length}
            onApply={() => {
                state.onChange({...state.value, isImageLoadingToEditor: true, errorMessage: ""});
                multiSelectOptions.onApply(imageArea, multiSelectOptions.selectedImages, modal.close);
            }}
            onClear={multiSelectOptions.onClearSelections}
        />
        )}

    </>))

    .run()

);
